import React, { useEffect, useMemo, useRef } from "react";
import { Avatar, Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import DriveFolderNameField from "../Drive/DriveFolderNameField";
import { useDriveFolderPickerOptions } from "../../hooks/useDriveFolderPickerOptions";
import {
  isNoticeComposerAudienceSelected,
  resolveNoticeAudienceKey,
  sanitizeDriveFolderMatchKey,
} from "../../utils/driveFolder";

export type SaveToDriveFolderOptionsProps = {
  audience: string;
  targetSubcircle?: string;
  staffOnly?: boolean;
  audienceLabel?: string;
  audienceAvatarSrc?: string;
  audienceAvatarText?: string;
  driveFolderKey: string;
  publishMode?: "now" | "schedule";
  disabled?: boolean;
  layout?: "stacked" | "inline" | "card";
  onDriveFolderKeyChange: (value: string) => void;
};

const SaveToDriveFolderOptions: React.FC<SaveToDriveFolderOptionsProps> = ({
  audience,
  targetSubcircle,
  staffOnly = false,
  audienceLabel = "",
  audienceAvatarSrc,
  audienceAvatarText = "",
  driveFolderKey,
  publishMode = "now",
  disabled = false,
  layout = "stacked",
  onDriveFolderKeyChange,
}) => {
  const theme = useTheme();
  const audienceSelected = isNoticeComposerAudienceSelected(
    audience,
    targetSubcircle,
    staffOnly,
  );
  const audienceKey = useMemo(
    () => resolveNoticeAudienceKey(audience, targetSubcircle),
    [audience, targetSubcircle],
  );
  const folderOptions = useDriveFolderPickerOptions(audience, targetSubcircle);
  const prevAudienceKeyRef = useRef(audienceKey);

  const circleLabel = audienceLabel.trim() || "this circle";
  const defaultFolderName = useMemo(
    () => sanitizeDriveFolderMatchKey(audienceLabel.trim()),
    [audienceLabel],
  );

  /** Circle name + current value in the list so defaults look like dropdown picks. */
  const pickerOptions = useMemo(() => {
    const merged = new Map<string, string>();

    const addOption = (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const key = sanitizeDriveFolderMatchKey(trimmed);
      if (!merged.has(key)) {
        merged.set(key, trimmed);
      }
    };

    folderOptions.forEach(addOption);
    if (defaultFolderName) {
      addOption(audienceLabel.trim() || defaultFolderName);
    }
    if (driveFolderKey.trim()) {
      addOption(driveFolderKey);
    }

    return Array.from(merged.values()).sort((a, b) => a.localeCompare(b));
  }, [folderOptions, defaultFolderName, audienceLabel, driveFolderKey]);

  useEffect(() => {
    const audienceChanged = prevAudienceKeyRef.current !== audienceKey;
    if (audienceChanged) {
      prevAudienceKeyRef.current = audienceKey;
    }

    if (!audienceSelected || !defaultFolderName) return;

    if (audienceChanged || !driveFolderKey.trim()) {
      onDriveFolderKeyChange(defaultFolderName);
    }
  }, [
    audienceKey,
    audienceSelected,
    defaultFolderName,
    driveFolderKey,
    folderOptions.length,
    onDriveFolderKeyChange,
  ]);

  const handleFolderChange = (raw: string) => {
    onDriveFolderKeyChange(sanitizeDriveFolderMatchKey(raw));
  };

  const helperText = driveFolderKey
    ? publishMode === "schedule"
      ? "Attachments will be added to this folder when the notice publishes."
      : "Attachments will be saved to this folder in Drive."
    : `Attachments will be saved in ${circleLabel}’s Drive folder automatically. The folder is created if needed.`;

  if (layout === "card") {
    return (
      <Box
        sx={{
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          p: 1.5,
        }}
      >
        {!audienceSelected ? (
          <Typography variant="body2" color="text.secondary">
            Choose who should receive this notice to pick a Drive folder.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Avatar
                src={audienceAvatarSrc}
                alt={circleLabel}
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                }}
              >
                {audienceAvatarText}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DriveFolderNameField
                  value={driveFolderKey}
                  onChange={handleFolderChange}
                  options={pickerOptions}
                  disabled={disabled}
                  compact
                  label=""
                  placeholder="Choose or type a folder name"
                />
              </Box>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block", lineHeight: 1.4, pl: 0.25 }}
            >
              Choose an existing folder, or type a new name to create one.
            </Typography>
          </>
        )}
      </Box>
    );
  }

  if (!audienceSelected) {
    return null;
  }

  if (layout === "inline") {
    return (
      <Box sx={{ width: "100%" }}>
        <DriveFolderNameField
          value={driveFolderKey}
          onChange={handleFolderChange}
          options={pickerOptions}
          disabled={disabled}
          compact
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block", lineHeight: 1.35 }}
        >
          {helperText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 400,
          color: "primary.main",
          fontSize: { xs: "1.125rem", sm: "1.25rem" },
          mb: 1,
        }}
      >
        Save to Drive folder
      </Typography>
      <DriveFolderNameField
        value={driveFolderKey}
        onChange={handleFolderChange}
        options={pickerOptions}
        disabled={disabled}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: -1.5, display: "block" }}
      >
        {helperText}
      </Typography>
    </Box>
  );
};

export default SaveToDriveFolderOptions;
